import type { ReactNode } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";

type DropZoneFeedbackProps = {
  actionError?: ReactNode;
  actionStatus?: ReactNode;
  actionSuccess?: ReactNode;
  dropError?: ReactNode;
  dropMessage: ReactNode;
  isDragActive: boolean;
};

type FeedbackMessageProps = {
  children: ReactNode;
  role: "alert" | "status";
  variant: "info" | "success" | "warning";
};

const VARIANT_ICON: Record<FeedbackMessageProps["variant"], ReactNode> = {
  info: (
    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
  ),
  success: <CheckCircle2 size={17} strokeWidth={2} aria-hidden="true" />,
  warning: <AlertCircle size={17} strokeWidth={2} aria-hidden="true" />,
};

const FeedbackMessage = ({ children, role, variant }: FeedbackMessageProps) => (
  <div
    className={`cle-banner cle-banner--${variant} mx-3 mb-3 px-3 py-2`}
    role={role}
  >
    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 mt-1">
      {VARIANT_ICON[variant]}
    </span>
    <div className="small flex-grow-1 min-w-0">{children}</div>
  </div>
);

const DropZoneFeedback = ({
  actionError,
  actionStatus,
  actionSuccess,
  dropError,
  dropMessage,
  isDragActive,
}: DropZoneFeedbackProps) => (
  <>
    {isDragActive ? (
      <div
        className="cle-rounded-card__drop-overlay"
        role="status"
        aria-live="polite"
      >
        <div className="cle-rounded-card__drop-message">{dropMessage}</div>
      </div>
    ) : null}
    {dropError ? (
      <FeedbackMessage role="alert" variant="warning">
        {dropError}
      </FeedbackMessage>
    ) : null}
    {actionSuccess ? (
      <FeedbackMessage role="status" variant="success">
        {actionSuccess}
      </FeedbackMessage>
    ) : null}
    {actionError ? (
      <FeedbackMessage role="alert" variant="warning">
        {actionError}
      </FeedbackMessage>
    ) : null}
    {actionStatus ? (
      <FeedbackMessage role="status" variant="info">
        {actionStatus}
      </FeedbackMessage>
    ) : null}
  </>
);

export default DropZoneFeedback;
